import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ApiError } from "../api/client";
import { getMemberDetail } from "../api/members";
import { useAuth } from "../auth/AuthContext";
import { MemberFinanceOperations } from "../components/MemberFinanceOperations";
import type { SessionCapabilities } from "../api/sessionContext";

type Target = { full_name: string; username: string | null };
type IdentityView = { target: Target; memberId: string; token: string } | null;

function MemberFinanceTarget({ memberId, token, capabilities, expireSession }: {
  memberId: string;
  token: string;
  capabilities: SessionCapabilities;
  expireSession(): void;
}) {
  const [identity, setIdentity] = useState<IdentityView>(null);
  const [error, setError] = useState<string | null>(null);
  const heading = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    let cancelled = false;
    setIdentity(null);
    setError(null);
    void getMemberDetail(token, memberId).then((target) => {
      if (!cancelled) setIdentity({ target, memberId, token });
    }).catch((cause: unknown) => {
      if (cancelled) return;
      if (cause instanceof ApiError && cause.status === 401) { expireSession(); return; }
      setError("Não foi possível confirmar o associado deste atendimento. Volte à ficha e verifique seu acesso.");
    });
    return () => { cancelled = true; };
  }, [token, memberId, expireSession]);

  useEffect(() => { if (identity) heading.current?.focus(); }, [identity]);

  const current = identity?.memberId === memberId && identity.token === token ? identity.target : null;
  return (
    <main id="main-content" className="member-finance">
      <header className="member-finance__identity">
        <p className="members-page__eyebrow">Secretaria / DAF · Atendimento financeiro</p>
        <h1 ref={heading} tabIndex={-1}>{current ? current.full_name : "Confirmando associado…"}</h1>
        <p>Operações vinculadas ao associado atendido. Instruções de pagamento permanecem no acesso do titular.</p>
        <Link className="member-finance__back" to={`/associados/${memberId}`}>Voltar à ficha do associado</Link>
      </header>
      {error && <div className="status-banner status-banner--error" role="alert">{error}</div>}
      {!current && !error && <p role="status" aria-live="polite">Carregando identificação do associado…</p>}
      {current && <MemberFinanceOperations key={`${memberId}:${token}`} memberId={memberId} accessToken={token} capabilities={{ readPayments: true, recordPayment: capabilities.record_payment === true, adjustPayment: capabilities.adjust_payment === true }} onSessionExpired={expireSession} />}
    </main>
  );
}

export function MemberFinancePage() {
  const { memberId } = useParams();
  const { state, expireSession } = useAuth();
  if (state.status !== "authenticated") return null;
  const capabilities = state.sessionContext?.capabilities;
  if (!capabilities?.read_payments) return <main id="main-content" className="member-finance"><p role="alert">Sem permissão para consultar o financeiro deste associado.</p></main>;
  if (!memberId) return null;
  return <MemberFinanceTarget key={`${memberId}:${state.accessToken}`} memberId={memberId} token={state.accessToken} capabilities={capabilities} expireSession={expireSession} />;
}
